import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { ref, get, set } from 'firebase/database';
import SEO from '../components/SEO';
import TemplateSelector from '../components/TemplateSelector';
import SaveAsTemplate from '../components/SaveAsTemplate';

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */
interface Branch { id: string; match: string; next: string; }

interface ResponseItem {
  id: string;
  type: 'say' | 'ask' | 'branch' | 'transfer' | 'hangup';
  content: string;       // what the bot speaks
  variable?: string;     // for ask — save answer as
  branches?: Branch[];   // for branch — conditional paths
  transferTo?: string;   // for transfer — phone number or agent
  next?: string;         // node id to go to after (for say/ask)
  askResponses?: ResponseItem[];
}

interface Trigger {
  trigger: string;
  keywords: string[];
  responses: ResponseItem[];
}

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const S = {
  page: { background: 'var(--bg-page)', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  input: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const },
  textarea: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const },
  card: { background: 'var(--bg-card)', borderRadius: '14px', border: '1px solid var(--bg-element)', padding: '20px', marginBottom: '16px' } as React.CSSProperties,
  respCard: { background: 'var(--bg-sidebar)', borderRadius: '10px', border: '1px solid var(--bg-element)', padding: '16px', marginBottom: '12px' } as React.CSSProperties,
  label: { display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: '6px' },
  btnPrimary: { padding: '8px 18px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' } as React.CSSProperties,
  btnDanger: { padding: '6px 12px', background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnGhost: { padding: '6px 14px', background: 'var(--bg-element)', color: 'var(--text-secondary)', border: '1px solid var(--bg-element)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnGreen: { padding: '6px 14px', background: '#10b98120', color: '#10b981', border: '1px solid #10b98140', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' } as React.CSSProperties,
};

const TYPE_COLORS: Record<string, string> = {
  say: '#10b981',
  ask: '#f59e0b',
  branch: '#8b5cf6',
  transfer: '#3b82f6',
  hangup: '#ef4444',
};

const TYPE_LABELS: Record<string, string> = {
  say: '🔊 Say (bot speaks)',
  ask: '❓ Ask (collect answer)',
  branch: '🔀 Branch (split by what visitor says)',
  transfer: '📞 Transfer to agent',
  hangup: '📴 End call',
};

/* ─────────────────────────────────────────────
   RESPONSE EDITOR
───────────────────────────────────────────── */
function ResponseEditor({ resp, rIdx, allTriggers, allVariables, onUpdate, onDelete, onMoveUp }: {
  resp: ResponseItem; rIdx: number; allTriggers: Trigger[]; allVariables: string[];
  onUpdate: (f: string, v: any) => void;
  onDelete: () => void;
  onMoveUp: () => void;
}) {
  const color = TYPE_COLORS[resp.type] || '#64748b';

  const addBranch = () => {
    const branches = [...(resp.branches || []), { id: Date.now().toString(), match: '', next: '' }];
    onUpdate('branches', branches);
  };

  const updateBranch = (bIdx: number, field: keyof Branch, value: string) => {
    const branches = [...(resp.branches || [])];
    (branches[bIdx] as any)[field] = value;
    onUpdate('branches', branches);
  };

  const deleteBranch = (bIdx: number) => {
    onUpdate('branches', (resp.branches || []).filter((_, i) => i !== bIdx));
  };

  return (
    <div style={{ ...S.respCard, borderLeft: `3px solid ${color}` }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ background: color + '20', color, fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '10px' }}>#{rIdx + 1}</span>
          <select value={resp.type} onChange={e => onUpdate('type', e.target.value)}
            style={{ ...S.input, width: 'auto', padding: '5px 10px', fontSize: '12px', color, appearance: 'none' as const }}>
            {Object.entries(TYPE_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {rIdx > 0 && <button onClick={onMoveUp} style={S.btnGhost}>↑</button>}
          <button onClick={onDelete} style={S.btnDanger}>Delete</button>
        </div>
      </div>

      {/* SAY */}
      {resp.type === 'say' && (
        <div>
          <label style={S.label}>What the bot says aloud</label>
          <textarea value={resp.content} onChange={e => onUpdate('content', e.target.value)} rows={3}
            placeholder="Type exactly what the voice bot will speak to the caller..."
            style={{ ...S.textarea, marginBottom: '8px' }} />
          {allVariables.length > 0 && (
            <div style={{ padding: '10px', background: '#10b98110', borderRadius: '8px', border: '1px solid #10b98130' }}>
              <div style={{ fontSize: '11px', color: '#10b981', marginBottom: '6px', fontWeight: '600' }}>Insert collected variable</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {allVariables.map(v => (
                  <button key={v} onClick={() => onUpdate('content', resp.content + ` {{${v}}}`)}
                    style={{ padding: '3px 8px', background: '#10b981', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '11px' }}>
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ASK */}
      {resp.type === 'ask' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div>
            <label style={S.label}>What the bot asks aloud</label>
            <textarea value={resp.content} onChange={e => onUpdate('content', e.target.value)} rows={2}
              placeholder="e.g. What is your name? Or please say your account number."
              style={S.textarea} />
          </div>
          <div>
            <label style={S.label}>Save answer as</label>
            <input value={resp.variable || ''} onChange={e => onUpdate('variable', e.target.value)}
              placeholder="e.g. name, account_number, complaint" style={S.input} />
          </div>
          {resp.variable && (
            <div style={{ fontSize: '11px', color: '#f59e0b', padding: '8px 12px', background: '#f59e0b10', borderRadius: '8px' }}>
              Caller's answer saved as <strong>{`{{${resp.variable}}}`}</strong> — use in any Say response
            </div>
          )}
        </div>
      )}

      {/* BRANCH */}
      {resp.type === 'branch' && (
        <div>
          <div>
            <label style={S.label}>What the bot says before listening</label>
            <textarea value={resp.content} onChange={e => onUpdate('content', e.target.value)} rows={2}
              placeholder="e.g. For sales, say Sales. For support, say Support. For billing, say Billing."
              style={{ ...S.textarea, marginBottom: '12px' }} />
          </div>
          <label style={S.label}>Branches — when caller says...</label>
          {(resp.branches || []).map((branch, bIdx) => (
            <div key={branch.id} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <input value={branch.match} onChange={e => updateBranch(bIdx, 'match', e.target.value)}
                placeholder="If caller says... (e.g. sales, one, yes)"
                style={{ ...S.input, flex: 1 }} />
              <span style={{ color: 'var(--text-faint)', fontSize: '13px', flexShrink: 0 }}>→ play trigger</span>
              <select value={branch.next} onChange={e => updateBranch(bIdx, 'next', e.target.value)}
                style={{ ...S.input, flex: 1, appearance: 'none' as const }}>
                <option value="">End call</option>
                {allTriggers.map(t => (
                  <option key={t.trigger} value={t.trigger}>{t.trigger}</option>
                ))}
              </select>
              <button onClick={() => deleteBranch(bIdx)} style={S.btnDanger}>✕</button>
            </div>
          ))}
          <button onClick={addBranch} style={{ ...S.btnGhost, marginTop: '4px' }}>+ Add branch</button>
          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '8px' }}>
            When the caller says a matching word, the bot jumps to that trigger's flow
          </div>
        </div>
      )}

      {/* TRANSFER */}
      {resp.type === 'transfer' && (
        <div style={{ background: '#3b82f610', border: '1px solid #3b82f630', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ color: '#3b82f6', fontSize: '12px', fontWeight: '600' }}>Transfers the caller to a live agent or phone number</div>
          <div>
            <label style={S.label}>What the bot says before transferring</label>
            <textarea value={resp.content} onChange={e => onUpdate('content', e.target.value)} rows={2}
              placeholder="e.g. Please hold while I connect you to our sales team."
              style={S.textarea} />
          </div>
          <div>
            <label style={S.label}>Transfer to phone number</label>
            <input value={resp.transferTo || ''} onChange={e => onUpdate('transferTo', e.target.value)}
              placeholder="e.g. +263771234567" style={S.input} />
          </div>
        </div>
      )}

      {/* HANGUP */}
      {resp.type === 'hangup' && (
        <div style={{ background: '#ef444410', border: '1px solid #ef444430', borderRadius: '8px', padding: '14px' }}>
          <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>Ends the call after saying goodbye</div>
          <label style={S.label}>Goodbye message</label>
          <textarea value={resp.content} onChange={e => onUpdate('content', e.target.value)} rows={2}
            placeholder="e.g. Thank you for calling. Have a great day! Goodbye." style={S.textarea} />
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function VoiceBuilder() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const isTemplate = window.location.pathname.includes('/template-builder/');
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [botName, setBotName] = useState('My Voice Bot');
  const [language, setLanguage] = useState('en-US');
  const [voiceType, setVoiceType] = useState<'woman' | 'man'>('woman');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTriggerWord, setNewTriggerWord] = useState('');
  const [newTriggerContent, setNewTriggerContent] = useState('');
  const [expandedTriggers, setExpandedTriggers] = useState<Set<number>>(new Set([0]));

  const allVariables = triggers.flatMap(t =>
    t.responses.filter(r => r.variable).map(r => r.variable as string)
  );

  useEffect(() => { loadFlow(); }, [botId]);

  const loadFlow = async () => {
    if (!botId) { setLoading(false); return; }
    try {
      const [botSnap, flowSnap] = await Promise.all([
        get(ref(db, `bots/${botId}`)),
        get(ref(db, isTemplate ? `templates/${botId}` : `flows/${botId}`)),
      ]);
      if (botSnap.exists()) {
        const b = botSnap.val();
        setBotName(b.businessName || 'My Voice Bot');
        if (b.voice) setVoiceType(b.voice);
      }
      if (flowSnap.exists()) {
        const d = flowSnap.val();
        setTriggers(d.triggers || []);
        if (d.language) setLanguage(d.language);
      } else {
        // Default voice flow
        setTriggers([
          {
            trigger: 'welcome',
            keywords: ['hi', 'hello', 'start'],
            responses: [
              {
                id: '1', type: 'branch',
                content: 'Hello! Welcome. For sales, say Sales. For support, say Support. For our hours, say Hours.',
                branches: [
                  { id: 'b1', match: 'sales', next: 'sales' },
                  { id: 'b2', match: 'support', next: 'support' },
                  { id: 'b3', match: 'hours', next: 'hours' },
                ],
              },
            ],
          },
          {
            trigger: 'sales',
            keywords: ['buy', 'purchase', 'pricing'],
            responses: [
              { id: '2', type: 'say', content: 'Our packages start from $15 per month. A sales agent will call you back within the hour.' },
              { id: '3', type: 'ask', content: 'What is your name?', variable: 'caller_name' },
              { id: '4', type: 'say', content: 'Thank you {{caller_name}}. We will be in touch very soon. Have a great day!' },
              { id: '5', type: 'hangup', content: 'Goodbye!' },
            ],
          },
          {
            trigger: 'hours',
            keywords: ['open', 'time', 'when'],
            responses: [
              { id: '6', type: 'say', content: 'We are open Monday to Friday, 8 AM to 5 PM. Closed on weekends and public holidays.' },
              { id: '7', type: 'hangup', content: 'Thank you for calling. Have a great day!' },
            ],
          },
          {
            trigger: 'support',
            keywords: ['help', 'problem', 'issue'],
            responses: [
              { id: '8', type: 'transfer', content: 'Please hold while I connect you to our support team.', transferTo: '+263771234567' },
            ],
          },
        ]);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveFlow = async () => {
    if (!auth.currentUser) { alert('Login required'); return; }
    setSaving(true);
    try {
      await set(ref(db, isTemplate ? `templates/${botId}` : `flows/${botId}`), {
        triggers, language, voiceType,
        botId, userId: auth.currentUser.uid, updatedAt: Date.now(),
      });
      alert('Saved!');
    } catch (e) { alert('Error: ' + e); }
    finally { setSaving(false); }
  };

  const toggleExpand = (idx: number) => {
    setExpandedTriggers(prev => {
      const next = new Set(Array.from(prev));
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const addTrigger = () => {
    if (!newTriggerWord.trim()) { alert('Enter a trigger word'); return; }
    const idx = triggers.length;
    setTriggers([...triggers, {
      trigger: newTriggerWord.toLowerCase().trim(),
      keywords: [],
      responses: [{ id: Date.now().toString(), type: 'say', content: newTriggerContent || '' }],
    }]);
    setNewTriggerWord('');
    setNewTriggerContent('');
    setExpandedTriggers(prev => new Set([...Array.from(prev), idx]));
  };

  const updateResponse = (tIdx: number, rIdx: number, field: string, value: any) => {
    const updated = [...triggers];
    updated[tIdx].responses[rIdx] = { ...updated[tIdx].responses[rIdx], [field]: value };
    setTriggers(updated);
  };

  const moveResponseUp = (tIdx: number, rIdx: number) => {
    if (rIdx === 0) return;
    const updated = [...triggers];
    [updated[tIdx].responses[rIdx - 1], updated[tIdx].responses[rIdx]] =
      [updated[tIdx].responses[rIdx], updated[tIdx].responses[rIdx - 1]];
    setTriggers(updated);
  };

  const applyTemplate = (tmpl: any) => {
    setTriggers(tmpl.triggers || []);
    if (tmpl.language) setLanguage(tmpl.language);
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-page)' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Loading voice flow...</div>
    </div>
  );

  return (
    <>
      <SEO title={`Edit Voice Bot | ${botName}`} description="" keywords="" type="website" noindex />
      <div style={S.page}>

        {/* Top bar */}
        <div style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--bg-element)', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <button onClick={() => navigate(isTemplate ? '/templates' : '/dashboard')} style={S.btnGhost}>← {isTemplate ? 'Templates' : 'Dashboard'}</button>
            <span style={{ fontSize: '18px' }}>🎙️</span>
            <span style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '700' }}>{botName}</span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select value={language} onChange={e => setLanguage(e.target.value)}
              style={{ ...S.input, width: 'auto', padding: '7px 12px', fontSize: '12px' }}>
              <option value="en-US">English (US)</option>
              <option value="en-GB">English (UK)</option>
              <option value="en-ZW">English (ZW)</option>
              <option value="sn-ZW">Shona</option>
              <option value="nd-ZW">Ndebele</option>
            </select>
            <select value={voiceType} onChange={e => setVoiceType(e.target.value as 'woman' | 'man')}
              style={{ ...S.input, width: 'auto', padding: '7px 12px', fontSize: '12px' }}>
              <option value="woman">👩 Woman</option>
              <option value="man">👨 Man</option>
            </select>
            <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>{triggers.length} triggers</div>
            <TemplateSelector botType="voice" onApply={applyTemplate} />
            {!isTemplate && <SaveAsTemplate botType="voice" triggers={triggers} language={language} />}
            <button onClick={saveFlow} disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : '💾 Save flow'}
            </button>
          </div>
        </div>

        <div style={{ maxWidth: '860px', margin: '0 auto', padding: '28px 20px' }}>

          {/* Legend */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px', padding: '14px 18px', background: 'var(--bg-card)', borderRadius: '10px', border: '1px solid var(--bg-element)' }}>
            {Object.entries(TYPE_COLORS).map(([type, color]) => (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{TYPE_LABELS[type]?.replace(/^[^ ]+ /, '')}</span>
              </div>
            ))}
          </div>

          {/* Add trigger */}
          <div style={{ ...S.card, borderStyle: 'dashed', borderColor: '#5B4FE840' }}>
            <div style={{ color: 'var(--text-secondary)', fontWeight: '600', fontSize: '13px', marginBottom: '12px' }}>Add a new trigger</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Trigger name / keyword</label>
                <input value={newTriggerWord} onChange={e => setNewTriggerWord(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addTrigger()}
                  placeholder="e.g. sales, billing, goodbye" style={S.input} />
              </div>
              <div style={{ flex: 2 }}>
                <label style={S.label}>First thing bot says</label>
                <input value={newTriggerContent} onChange={e => setNewTriggerContent(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addTrigger()}
                  placeholder="e.g. Thank you for calling sales..." style={S.input} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button onClick={addTrigger} style={S.btnPrimary}>+ Add</button>
              </div>
            </div>
          </div>

          {triggers.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-faint)', fontSize: '14px' }}>
              No triggers yet. Add one above to build your voice flow.
            </div>
          )}

          {triggers.map((trigger, tIdx) => {
            const isExpanded = expandedTriggers.has(tIdx);
            return (
              <div key={tIdx} style={S.card}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <button onClick={() => toggleExpand(tIdx)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px', padding: '4px 0', flexShrink: 0, marginTop: '16px' }}>
                    {isExpanded ? '▾' : '▸'}
                  </button>

                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Trigger name</label>
                    <input value={trigger.trigger}
                      onChange={e => { const u = [...triggers]; u[tIdx].trigger = e.target.value.toLowerCase(); setTriggers(u); }}
                      style={{ ...S.input, fontWeight: '600', color: '#818cf8' }} />
                  </div>

                  <div style={{ flex: 2 }}>
                    <label style={S.label}>Voice keyword aliases (comma-separated)</label>
                    <input
                      value={trigger.keywords?.join(', ') || ''}
                      onChange={e => { const u = [...triggers]; u[tIdx].keywords = e.target.value.split(',').map(k => k.trim().toLowerCase()).filter(Boolean); setTriggers(u); }}
                      placeholder="e.g. one, buy, purchase"
                      style={S.input}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginTop: '18px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-faint)', padding: '4px 10px', background: 'var(--bg-element)', borderRadius: '10px' }}>
                      {trigger.responses.length} step{trigger.responses.length !== 1 ? 's' : ''}
                    </span>
                    <button onClick={() => {
                      const src = triggers[tIdx];
                      setTriggers([...triggers, { ...src, trigger: src.trigger + '_copy', responses: src.responses.map(r => ({ ...r, id: Date.now().toString() + Math.random() })) }]);
                    }} style={S.btnGhost}>📋</button>
                    <button onClick={() => {
                      if (!window.confirm(`Delete "${trigger.trigger}"?`)) return;
                      setTriggers(triggers.filter((_, i) => i !== tIdx));
                    }} style={S.btnDanger}>Delete</button>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: '600' }}>STEPS (executed in order)</div>
                      <button onClick={() => {
                        const u = [...triggers];
                        u[tIdx].responses.push({ id: Date.now().toString(), type: 'say', content: '' });
                        setTriggers(u);
                      }} style={S.btnGreen}>+ Add step</button>
                    </div>

                    {trigger.responses.map((resp, rIdx) => (
                      <ResponseEditor
                        key={resp.id}
                        resp={resp} rIdx={rIdx}
                        allTriggers={triggers}
                        allVariables={allVariables}
                        onUpdate={(f, v) => updateResponse(tIdx, rIdx, f, v)}
                        onDelete={() => { const u = [...triggers]; u[tIdx].responses = u[tIdx].responses.filter((_, i) => i !== rIdx); setTriggers(u); }}
                        onMoveUp={() => moveResponseUp(tIdx, rIdx)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
